import { Button, Stack, Typography } from "@mui/material";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { Panel } from "@poseidon/ui/components/Panel";
import { niceDate } from "@poseidon/utils/date";
import { JsonViewer } from "@textea/json-viewer";
import { useCallback, useEffect, useState } from "react";
import { AmlCheck } from "./AmlCheck";
import { useAmlChecks } from "./hooks/useAmlChecks";
import { RunAmlCheckButton } from "./RunAmlCheckButton";
import { SelectDialog } from "@poseidon/ui/components/SelectDialog";
import { ShowMoreDialog } from "@poseidon/ui/components/ShowMoreDialog";
import CodeIcon from "@mui/icons-material/Code";

const mapAmlCheckToSelectDialogOption = ({ id, requestedAt }) =>
  requestedAt
    ? {
        label: niceDate(requestedAt),
        secondary: requestedAt.toLocaleString(),
        value: id,
      }
    : "";

export const AmlChecks = ({ sailorId }) => {
  const [amlCheck, setAmlCheck] = useState(null);
  const { setError } = useErrorMessage();

  const { amlChecks, loading, error } = useAmlChecks(sailorId);

  useEffect(() => {
    if (!loading && amlChecks.length > 0) {
      setAmlCheck(amlChecks[0]);
    }

    if (error) {
      setError(error);
    }
  }, [error, setError, amlChecks, loading]);

  const selectAmlCheckOnChange = useCallback(
    (value) => setAmlCheck(amlChecks.find((check) => check.id === value)),
    [amlChecks]
  );

  return (
    !loading && (
      <Panel
        title="Checks"
        expandable
        actions={
          <>
            {amlCheck && (
              <ShowMoreDialog
                buttonText="View raw data"
                buttonVariant="outlined"
                title="Raw Search Data"
                buttonProps={{
                  size: "small",
                  startIcon: <CodeIcon />,
                }}
              >
                <JsonViewer value={amlCheck ?? {}} displayDataTypes={false} />
              </ShowMoreDialog>
            )}
            <RunAmlCheckButton
              sailorId={sailorId}
              isFirstCheck={!amlChecks?.length}
            />
          </>
        }
      >
        {amlChecks?.length < 1 && (
          <Typography variant="caption" sx={{ mb: 2, opacity: 0.7 }}>
            No KYC/AML Checks created yet
          </Typography>
        )}

        <Stack spacing={3} sx={{ mb: 2 }}>
          <Typography variant="body2">
            {amlCheck?.requestedAt && (
              <>
                Check from {niceDate(amlCheck?.requestedAt)} (
                {amlCheck?.requestedAt.toLocaleString()})
              </>
            )}
            {amlChecks.length > 0 && (
              <SelectDialog
                buttonText="View other"
                buttonVariant="text"
                title="Select Credit Check"
                buttonProps={{ size: "small" }}
                options={amlChecks}
                mapOption={mapAmlCheckToSelectDialogOption}
                onChange={selectAmlCheckOnChange}
                value={amlCheck}
              />
            )}
            {amlChecks.length > 1 && amlCheck?.id !== amlChecks[0].id && (
              <Button
                variant="text"
                size="small"
                onClick={() => setAmlCheck(amlChecks[0])}
              >
                View latest
              </Button>
            )}
          </Typography>
          {amlCheck && <AmlCheck amlCheck={amlCheck} sailorId={sailorId} />}
        </Stack>
      </Panel>
    )
  );
};
